import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useError } from "../components/ErrorContext";
import { validateAndRenewIfNeeded } from "./TokenUtilities";
import { cookieOptions } from "./BasicUtilities";
import Cookies from 'js-cookie';

// Concurrent identical GETs (same URL + auth mode) share a single network request
// instead of each firing their own. This collapses the common duplicate-fetch cases:
// two always-mounted components requesting the same endpoint (e.g. NavBar and
// EmailVerifyBanner both hitting /auth/me), and React StrictMode double-invoking an
// effect in dev. The entry is removed as soon as the request settles, so a later
// navigation still refetches — this is in-flight de-duplication, NOT a response cache.
const inFlightGets = new Map();

// Global in-flight request counter with a tiny pub/sub, so a single app-wide spinner can
// reflect "any API call is pending" without every page wiring its own loading state. Every
// logical CallApi increments on start and decrements when it settles (dedup hits piggyback on
// the original request and are not double-counted).
let pendingRequests = 0;
const loadingListeners = new Set();
function emitLoading() {
    const busy = pendingRequests > 0;
    loadingListeners.forEach((fn) => fn(busy));
}
function trackStart() { pendingRequests += 1; emitLoading(); }
function trackEnd() { pendingRequests = Math.max(0, pendingRequests - 1); emitLoading(); }

// Subscribe to loading changes. Calls back with the current state immediately, and returns an
// unsubscribe function. Used by GlobalLoadingIndicator.
export function subscribeApiLoading(fn) {
    loadingListeners.add(fn);
    fn(pendingRequests > 0);
    return () => loadingListeners.delete(fn);
}

export function useApiCaller() {
    const { showError, showSuccess, showUpgrade } = useError();
    const { logoutState } = useUser();
    const navigate = useNavigate();

    const depsRef = useRef({ showError, showSuccess, showUpgrade, logoutState, navigate });
    depsRef.current = { showError, showSuccess, showUpgrade, logoutState, navigate };

    return useCallback(function CallApi(args) {
        const { url, method = 'GET', token = true, spinner = true } = args;
        // Only de-dupe idempotent GETs, and only while the request is in flight.
        const dedupeKey = method === 'GET' ? `${token === false ? 'anon' : 'auth'}|${url}` : null;
        if (dedupeKey && inFlightGets.has(dedupeKey)) {
            return inFlightGets.get(dedupeKey);
        }

        const promise = performCall(args, CallApi, depsRef.current);
        // Reflect this request in the global spinner for its whole lifetime — unless the caller
        // opts out (`spinner: false`) because it renders its own inline progress UI (e.g. login).
        if (spinner !== false) {
            trackStart();
            promise.finally(trackEnd);
        }

        if (dedupeKey) {
            inFlightGets.set(dedupeKey, promise);
            promise.finally(() => {
                // Guard against clobbering a newer entry (shouldn't happen while in
                // flight, but keeps the map correct if timing ever overlaps).
                if (inFlightGets.get(dedupeKey) === promise) inFlightGets.delete(dedupeKey);
            });
        }
        return promise;
    }, []);
}

async function performCall(
    { url, method = 'GET', payload = null, token = true, silent = false },
    CallApi,
    deps,
) {
    const { showError, showSuccess, showUpgrade, logoutState, navigate } = deps;
    const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
    const headers = {};
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }

    if (token) {
        const jwt_token = Cookies.get('jwt_token');
        const jwt_token_to_use = await validateAndRenewIfNeeded(jwt_token, CallApi); // in this function, refresh token may also be set
        if (!jwt_token_to_use) {
            // Session expired: show a green (success-styled) notice and redirect to login.
            showSuccess("Your session has expired. Redirecting you to login…");
            logoutState();
            navigate('/login');
            return {
                success: false,
                status: 401,
                message: "JWT token could not be refreshed",
                data: null,
            };
        }
        headers['x-auth-jwt'] = jwt_token_to_use;
        Cookies.set('jwt_token', jwt_token_to_use, cookieOptions());
    }

    const options = {
        method,
        headers,
    };

    if (payload) {
        options.body = isFormData ? payload : JSON.stringify(payload);
    }

    try {
        const response = await fetch(url, options);

        const contentType = response.headers.get('content-type');

        const data = contentType && contentType.includes('application/json')
            ? await response.json()
            : await response.text();
        if (!response.ok) {
            let errorMessage = data?.errorMessage || 'Request failed';
            // A 401 on an authenticated call means the token was invalidated server-side
            // (forced logout, password change, account deactivated) even though the client
            // still had a seemingly-valid JWT. Treat it as a clean session end.
            if (token && !silent && response.status === 401) {
                showSuccess("Your session has ended. Please log in again.");
                logoutState();
                navigate('/login');
                return { success: false, status: 401, message: errorMessage, data };
            }
            // `silent` calls (e.g. token refresh) handle their own failure — the
            // caller shows the graceful "session expired" notice, so don't paint a red error.
            if (!silent) {
                // 402 = plan/paywall gate: show a friendly upgrade nudge, not a red error.
                if (response.status === 402) {
                    showUpgrade(errorMessage);
                } else {
                    showError(errorMessage);
                }
            }
            return {
            success: false,
            status: response.status,
            message: errorMessage,
            data: data,
            };
        }

        return {
            success: true,
            status: response.status,
            data: data,
        };
    } catch (error) {
        let errorMessage = error.message || 'Unknown error occurred';
        // Suppress the red toast for silent calls (e.g. token refresh on an expired
        // session, which can throw "Failed to fetch") — the caller handles it gracefully.
        if (!silent) {
            showError(errorMessage);
        }
        return {
            success: false,
            status: error.status || 500,
            message: errorMessage,
            data: error.data || null,
        };
    }
}
